import { getParticipantLayoutRoomConfig } from "./participant-layout-room-config";

export const PARTICIPANT_LAYOUT_FORCED_STATE = {
  NONE: "none",
  OPEN: "open",
  CLOSED: "closed"
};

export const PARTICIPANT_LAYOUT_USER_DATA_KEY = "participant_layout";

export function normalizeParticipantLayoutSpotlightTileId(spotlightTileId) {
  return typeof spotlightTileId === "string" && spotlightTileId.trim() ? spotlightTileId : null;
}

export function normalizeParticipantLayoutForcedState(forcedState) {
  return Object.values(PARTICIPANT_LAYOUT_FORCED_STATE).includes(forcedState)
    ? forcedState
    : PARTICIPANT_LAYOUT_FORCED_STATE.NONE;
}

export function getParticipantLayoutRoomStateFromConfig(participantLayoutConfig = {}) {
  const roomEnabled = !!participantLayoutConfig.enabled;
  const { defaultEnabled, forceEnabled } = getParticipantLayoutRoomConfig(participantLayoutConfig);
  const forcedState =
    roomEnabled && forceEnabled ? PARTICIPANT_LAYOUT_FORCED_STATE.OPEN : PARTICIPANT_LAYOUT_FORCED_STATE.NONE;

  return {
    roomEnabled,
    layoutEnabled: roomEnabled && (forcedState === PARTICIPANT_LAYOUT_FORCED_STATE.OPEN || defaultEnabled),
    forcedState,
    spotlightTileId: null
  };
}

export function getParticipantLayoutRoomStateFromHub(hub) {
  const participantLayoutState = hub?.user_data?.[PARTICIPANT_LAYOUT_USER_DATA_KEY];

  if (!participantLayoutState || typeof participantLayoutState !== "object") {
    return null;
  }

  const roomEnabled = !!participantLayoutState.enabled;

  return {
    roomEnabled,
    forcedState: roomEnabled
      ? normalizeParticipantLayoutForcedState(participantLayoutState.forced_state)
      : PARTICIPANT_LAYOUT_FORCED_STATE.NONE,
    spotlightTileId: roomEnabled
      ? normalizeParticipantLayoutSpotlightTileId(participantLayoutState.spotlight_tile_id)
      : null
  };
}

export function getParticipantLayoutRoomStateFromConfigAndHub(participantLayoutConfig = {}, hub) {
  const configState = getParticipantLayoutRoomStateFromConfig(participantLayoutConfig);
  const sharedState = getParticipantLayoutRoomStateFromHub(hub);

  if (!sharedState) {
    return configState;
  }

  if (configState.forcedState === PARTICIPANT_LAYOUT_FORCED_STATE.OPEN) {
    return {
      roomEnabled: configState.roomEnabled,
      forcedState: configState.roomEnabled ? PARTICIPANT_LAYOUT_FORCED_STATE.OPEN : PARTICIPANT_LAYOUT_FORCED_STATE.NONE,
      spotlightTileId: configState.roomEnabled ? sharedState.spotlightTileId : null,
      layoutEnabled: configState.roomEnabled
    };
  }

  const roomEnabled = configState.roomEnabled && sharedState.roomEnabled;
  const forcedState = roomEnabled ? sharedState.forcedState : PARTICIPANT_LAYOUT_FORCED_STATE.NONE;

  return {
    roomEnabled,
    forcedState,
    spotlightTileId: roomEnabled ? sharedState.spotlightTileId : null,
    layoutEnabled:
      roomEnabled &&
      (forcedState === PARTICIPANT_LAYOUT_FORCED_STATE.OPEN ||
        (forcedState === PARTICIPANT_LAYOUT_FORCED_STATE.NONE && configState.layoutEnabled))
  };
}

export function createParticipantLayoutRoomStateForEnabledChange({ forcedState, spotlightTileId } = {}, roomEnabled) {
  const normalizedRoomEnabled = !!roomEnabled;

  return {
    roomEnabled: normalizedRoomEnabled,
    forcedState: normalizedRoomEnabled
      ? normalizeParticipantLayoutForcedState(forcedState)
      : PARTICIPANT_LAYOUT_FORCED_STATE.NONE,
    spotlightTileId: normalizedRoomEnabled ? normalizeParticipantLayoutSpotlightTileId(spotlightTileId) : null
  };
}

export function createParticipantLayoutRoomStateForForcedStateChange(forcedState, { spotlightTileId } = {}) {
  return {
    roomEnabled: true,
    forcedState: normalizeParticipantLayoutForcedState(forcedState),
    spotlightTileId: normalizeParticipantLayoutSpotlightTileId(spotlightTileId)
  };
}

export function createParticipantLayoutRoomStateForSpotlightChange(spotlightTileId) {
  return {
    roomEnabled: true,
    spotlightTileId: normalizeParticipantLayoutSpotlightTileId(spotlightTileId)
  };
}

export function createHubSettingsForParticipantLayoutRoomState(hub, { roomEnabled, forcedState, spotlightTileId }) {
  const normalizedRoomEnabled = !!roomEnabled;
  const normalizedForcedState = normalizedRoomEnabled
    ? normalizeParticipantLayoutForcedState(forcedState)
    : PARTICIPANT_LAYOUT_FORCED_STATE.NONE;
  const normalizedSpotlightTileId = normalizedRoomEnabled
    ? normalizeParticipantLayoutSpotlightTileId(spotlightTileId)
    : null;

  return {
    name: hub.name,
    description: hub.description,
    room_size: hub.room_size,
    entry_mode: hub.entry_mode,
    member_permissions: hub.member_permissions,
    user_data: {
      ...(hub.user_data || {}),
      [PARTICIPANT_LAYOUT_USER_DATA_KEY]: {
        enabled: normalizedRoomEnabled,
        forced_state: normalizedForcedState,
        spotlight_tile_id: normalizedSpotlightTileId
      }
    }
  };
}
